<script>
	/**
	 * This top-level route displays a list of all the student's assignments for
	 * all classes.
	 *
	 * PROPS
	 * - route-context - the context from page.js
	 * - on-ready - a function to call when the route is ready
	 *
	 * SUB ROUTES
	 *
	 * None. Switching article tabs from "Assignments to do" to "submitted assignments" simply
	 * results in a different query being sent to the server. There are no sub-routes per se.
	 */
	const {
		getOfflineModeStatus,
		downloadAssignment,
		clearAssignment,
		clearOldAssignments,
		updateOfflineAssignments,
		getAllOfflineAssignmentData,
		uploadOfflineWorkspaces,
		clearAll
	} = require('utilities/offlineUtilities');
	const scrollManager = require('ui/core/scrollManager');
	const userContext = require('core/userContext');
	const utils = require('../../utilities.js');
	const classService = require('services/classService');
	const accountService = require('services/accountService');
	const assignmentService = require('services/assignmentService');
	const Q = require('q');
	const _ = require('underscore');
	const moment = require('moment');
	const page = require('page');
	const model = require('model/model');

	const BIG_BOOK_OF_KNOWLEDGE_TAB_FILTER = 'bbok';
	const ASSIGNMENTS_SUBMITTED_TAB_FILTER = 's'; // submitted assignments tab filter
	const ASSIGNMENTS_TO_DO_TAB_FILTER = 'c'; // assignments to do tab filter: student always starts here

	// const surveyUtilities = require('utilities/surveyUtilities');
	// const studentGetCVAAssignment = surveyUtilities.studentGetCVAAssignment;

	const { getStudentResearchAssessments } = require('utilities/studentResearchAssessmentUtilities');

	const studentKnowledgeInventoryUtilities = require('utilities/studentKnowledgeInventoryUtilities');
	const studentGetSKIAssignment = studentKnowledgeInventoryUtilities.studentGetSKIAssignment;

	const googleClassroomUtilities = require('utilities/googleClassroomUtilities');
	const authenticateWithGoogleClassroom = googleClassroomUtilities.authenticateWithGoogleClassroom;

	const alertUtilities = require('utilities/alertUtilities');

	const analyticsUtilities = require('utilities/analyticsUtilities');
	const fireEvent = analyticsUtilities.fireEvent;
	const SL_LIBRARY_OPEN = analyticsUtilities.events.SL_LIBRARY_OPEN;

	const { getNotifications, readNotification } = require('utilities/studentNotificationUtilities');

	const pubsub = require('events/pubsub');
	const beatriceDataMap = require('beatrice-data-map');
	const logger = require('utilities/logger');
	const {
		sortAssignmentsBySubmittedThenCreationTime,
		getAssignmentDisplayTime,
		isAssignmentSubmitted
	} = require('utilities/studentAssignmentUtilities');

	const tabCfg = [
		{
			text: 'Assignments To Do',
			id: ASSIGNMENTS_TO_DO_TAB_FILTER
		},
		{
			text: 'Submitted Assignments',
			id: ASSIGNMENTS_SUBMITTED_TAB_FILTER
		},
		{
			text: 'My Big Book of Knowledge',
			id: BIG_BOOK_OF_KNOWLEDGE_TAB_FILTER
		}
	];

	module.exports = {
		props: ['on-ready', 'route-context'],
		components: {
			'article-image': require('components/article-image.vue').default,
			'overdue-icon': require('components/overdue-icon.vue').default,
			'graded-icon': require('components/graded-icon.vue').default,
			pagination: require('components/pagination.vue').default,
			'big-book-of-knowledge': require('components/big-book-of-knowledge.vue').default,
			'tab-container': require('components/tab-container.vue').default,
			'article-result-content': require('components/article-result-content.vue').default,
			'sign-up-icon': require('components/svg/sign-up-icon.vue').default,
			'smiley-icon': require('components/svg/smiley-icon.vue').default,
			'assessment-icon': require('components/svg/assessment-icon.vue').default,
			'offline-mode-toggle': require('components/offline-mode-toggle.vue').default,
			'download-assignment-modal': require('components/download-assignment-modal.vue').default,
			'form-submitter': require('components/form-submitter.vue').default
		},
		modelData: {
			studentCVAAssignment: 'studentCVAAssignment',
			studentTab: 'studentTab',
			studentOffset: 'offset',
			skiAssignment: 'skiAssignment',
			researchAssessments: 'researchAssessments',
			activeResearchAssessment: 'activeResearchAssessment',
			activeClass: 'activeClass',
			studentNotifications: 'studentNotifications',
			offlineMode: 'offlineMode',
			offlineAssignments: 'offlineAssignments',
			offlineWorkspaceData: 'offlineWorkspaceData',
			userContext: 'userContext',
			localGCAccessToken: 'localGCAccessToken',
			workspaceUploadFailures: 'workspaceUploadFailures',
			offlineModeAvailable: 'offlineModeAvailable',
			hasDownloadedAnAssignment: 'hasDownloadedAnAssignment',
			hideStudentLibraryPromo: 'hideStudentLibraryPromo'
		},
		data: function () {
			return {
				classes: [],
				classCode: '', // the class code
				className: '', // the name of the class
				assignments: [],
				pageSize: 5,
				pages: [],
				pagesJumpLast: null,
				pagesJumpFirst: null,

				fetchingAssignments: false,

				BIG_BOOK_OF_KNOWLEDGE_TAB_FILTER,
				ASSIGNMENTS_SUBMITTED_TAB_FILTER,
				ASSIGNMENTS_TO_DO_TAB_FILTER,

				tabs: tabCfg.slice(0, 2),

				bokAssigned: false,
				addClassForm: {},
				offlineLoading: {},
				uploadingWorkspaces: false,
				researchAssessment: null
			};
		},
		computed: {
			currentPage() {
				let offset = this.offset;
				let pageSize = this.pageSize;

				return offset / pageSize;
			},
			activeAssignmentsMap() {
				let assignments = this.assignments;
				let now = new Date().getTime() / 1000;
				let assignmentMap = {};

				if (assignments && assignments.length) {
					assignmentMap = assignments.reduce((acc, a) => {
						if ((!a.d || a.d <= now) && !this.isAssignmentSubmitted(a)) {
							acc[a.aid] = a;
						}
						return acc;
					}, {});
				}

				return assignmentMap;
			},
			filteredAssignments() {
				let studentTab = this.studentTab;
				let assignments = this.assignments;
				let now = new Date().getTime() / 1000;
				let filteredAssignments = [];

				if (studentTab !== BIG_BOOK_OF_KNOWLEDGE_TAB_FILTER) {
					assignments.forEach(a => {
						if (!a.d || a.d <= now) {
							if (studentTab === ASSIGNMENTS_TO_DO_TAB_FILTER && !this.isAssignmentSubmitted(a)) {
								filteredAssignments.push(a);
							} else if (studentTab === ASSIGNMENTS_SUBMITTED_TAB_FILTER && this.isAssignmentSubmitted(a)) {
								filteredAssignments.push(a);
							}
						}
					});
				}

				return filteredAssignments;
			},
			assignmentPage() {
				let offset = this.offset;
				let pageSize = this.pageSize;
				let filteredAssignments = this.filteredAssignments;
				let assignmentPage = [];

				assignmentPage = filteredAssignments.slice(offset, offset + pageSize);

				return assignmentPage;
			},
			library() {
				let activeClass = this.activeClass;
				return activeClass && activeClass.as && activeClass.as.library;
			},
			notificationCount() {
				let notifications = this.notifications || {};

				return Object.keys(notifications).filter(aid => notifications[aid]).length;
			},
			notifications() {
				let notifs = {};
				let studentNotifications = this.studentNotifications;
				let assignmentsResultsStateMap = (this.assignments || []).reduce((acc, a) => {
					acc[a.aid] = a.shwres;
					return acc;
				}, {});

				if (studentNotifications) {
					notifs = Object.keys(studentNotifications).reduce((acc, aid) => {
						if (assignmentsResultsStateMap[aid] !== false) {
							acc[aid] = studentNotifications[aid];
						}
						return acc;
					}, {});
				}

				return notifs;
			},
			offlineAssignmentsMap() {
				let offlineAssignments = this.offlineAssignments;
				let uid = this.userContext ? this.userContext.uid : null;
				let classCode = this.userContext ? this.userContext.classCode : null;
				let map = {};

				if (uid && offlineAssignments && offlineAssignments[uid]) {
					let allMap = offlineAssignments[uid];
					map = Object.keys(allMap).reduce((acc, aid) => {
						if (allMap[aid].cc === classCode) {
							acc[aid] = allMap[aid];
						}
						return acc;
					}, {});
				}
				return map;
			},
			userOfflineWorkspaceData() {
				let offlineWorkspaceData = this.offlineWorkspaceData;
				let uid = this.userContext ? this.userContext.uid : null;
				let data = null;

				if (uid && offlineWorkspaceData && offlineWorkspaceData[uid]) {
					data = offlineWorkspaceData[uid];
				}
				return data;
			},
			offlineModeInProgress() {
				let offlineMode = this.offlineMode;
				let userOfflineWorkspaceData = this.userOfflineWorkspaceData;

				return offlineMode || !!userOfflineWorkspaceData;
			},
			needsGoogleAuth() {
				let token = this.localGCAccessToken;
				let activeClass = this.activeClass;

				return activeClass && activeClass.gcid && !token;
			},
			showOfflineToggle() {
				let uc = userContext.getUserData();
				let offlineMode = this.offlineMode;
				let offlineModeAvailable = this.offlineModeAvailable;
				return offlineMode === true || (offlineModeAvailable && uc && uc.classCode && typeof offlineMode !== 'undefined');
			},
			offlineDisabled() {
				let offlineMode = this.offlineMode;
				let offlineAssignmentsMap = this.offlineAssignmentsMap;
				let showOfflineToggle = this.showOfflineToggle;
				let offlineAssignmentIds = Object.keys(offlineAssignmentsMap || {});
				let activeAssignmentsMap = this.activeAssignmentsMap;
				let activeOfflineAssignmentIds = offlineAssignmentIds.filter(a => activeAssignmentsMap[a]);

				return offlineMode !== true && (!showOfflineToggle || activeOfflineAssignmentIds.length < 1);
			},
			anyOfflineLoading() {
				let offlineLoading = this.offlineLoading;
				let keys = Object.keys(offlineLoading);
				let loading = false;
				if (keys && keys.length) {
					loading = keys.some(k => offlineLoading[k]);
				}
				return loading;
			},
			showStudentSidebar() {
				const { uid } = this.userContext || {};
				if (!uid) {
					return false;
				}

				const hideStudentLibraryPromo = Array.isArray(this.hideStudentLibraryPromo) && this.hideStudentLibraryPromo.includes(this.userContext.uid);
				return this.offlineModeInProgress === false && this.library && !hideStudentLibraryPromo;
			},
			showButterBar() {
				return this.offlineMode === false && !this.fetchingAssignments && this.studentTab === ASSIGNMENTS_TO_DO_TAB_FILTER;
			},
			sraButterBarContent() {
				const { type, sraid } = this.researchAssessment;
				const activeSRA = this.activeResearchAssessment;
				const baseBodyText = 'Please remember that this assessment is NOT graded. Just do your best!';
				const bodyTextAgreement = 'Clicking START shows your agreement to join this study.';

				const contentDict = {
					vabka: {
						title: 'Vocabulary & Background Knowledge Assessment',
						bodyText: `${bodyTextAgreement} ${baseBodyText}`
					},
					'vabka-post-assessment': {
						title: 'Vocabulary & Background Knowledge Assessment',
						bodyText: baseBodyText
					},
					va: {
						title: 'Vocabulary Assessment',
						bodyText: `${bodyTextAgreement} ${baseBodyText}`
					},
					'va-post-assessment': {
						title: 'Vocabulary Assessment',
						bodyText: baseBodyText
					},
					bka: {
						title: 'Background Knowledge Assessment',
						bodyText: baseBodyText
					},
					'bka-post-assessment': {
						title: 'Background Knowledge Assessment',
						bodyText: baseBodyText
					},
					waata: {
						title: 'Write About a Topic'
					},
					'waata-post-assessment': {
						title: 'Write About a Topic'
					}
				};
				const unversionedType = type.split(/-\d/)[0];
				const content = contentDict[unversionedType];

				if (!content) {
					return null;
				}

				const { title, bodyText } = content;

				const inProgress = activeSRA?.sraid === sraid;
				const buttonText = inProgress ? 'Continue' : 'Start';

				return {
					title,
					bodyText,
					buttonText
				};
			},
			showEmptyMessage() {
				return this.assignmentPage.length === 0 && !this.skiAssignment && !this.researchAssessment;
			}
		},
		methods: {
			getLink: function (item, assignment) {
				if (!item.aid) {
					// I don't this should trigger here, but we are getting messages of users navigating
					// to /student-workspace/undefined, so hopefully this will provide more info
					logger.error('Item has no aid', JSON.stringify(item));
				}

				if (item.pmd.pt === 'AAD') {
					if ((!item.inProgress || item.inProgress === true) && !this.isAssignmentSubmitted(item)) {
						// FIXME: item.aid is undefined sometimes
						return '/article-dashboard/' + item.aid;
					} else if (this.isAssignmentSubmitted(item)) {
						return `/student-workspace/${item.aid}#!wsTab:results`;
					} else {
						return '/student-workspace/' + item.aid + (item.inProgress && item.inProgress.pid ? '/' + item.inProgress.pid : '');
					}
				} else if (item.submitted) {
					return '/student-workspace/' + item.aid + '#!wsTab:text/';
				} else {
					return '/student-workspace/' + item.aid;
				}
			},
			addClass: function (data) {
				var self = this,
					classCode = data.classCode;

				return accountService
					.initializeGoogleUser({
						classCode: classCode,
						gcAddon: !!this.gcAddon
					})
					.then(function (result) {
						userContext.setToken(result.token);

						return classService.getClassesFromGoogleToken().then(function (results) {
							self.classes = utils.sortObjects(results.classes, 'name');

							utils.closeModals();

							return results;
						});
					})
					.fail(function (error) {
						if (error.code === 1060) {
							utils.openModal('#sr-tr-redirect');

							self.$subscribeOnce('modal-closed', function (modalId) {
								if (modalId === 'sr-tr-redirect') {
									page('/student-authentication?classCode=' + classCode);
								}
							});
						} else {
							throw error;
						}
					});
			},
			myClasses: function () {
				page('/my-classes');
			},
			/**
			 * Gets student assignments.
			 * @param cb	Callback signature function (err, ok), where
			 * err is a system error. ok (boolean) is true when
			 * the assignments (even though none) have been retrieved
			 * and false if, e.g., the student is not logged in.
			 * When 'ok' is true, assignments contains the assignment
			 * properties (see testClass.js in nodejs app test suite).
			 */
			getAssignments: function (offlineAssignments = []) {
				let now = new Date().getTime() / 1000;
				let lastYear = moment().subtract(1, 'year').toDate().getTime() / 1000;
				let offlineMode = this.offlineMode;

				if (!userContext.loggedIn()) {
					return Q.reject();
				} else if (!this.fetchingAssignments) {
					this.fetchingAssignments = true;
					return assignmentService
						.studentGetClassAssignments()
						.then(assignments => {
							this.className = assignments.cn;
							this.classCode = userContext.getStudentClassCode();

							let assignmentsToUpdate = [];
							let offlineAssignmentMap = offlineAssignments.reduce((acc, a) => {
								acc[a.aid] = a;
								return acc;
							}, {});

							this.sortAssignments(assignments.a, offlineAssignmentMap, offlineMode);

							this.assignments.forEach(i => {
								i.overdue = false;
								i.pmd.productType = i.pmd.pt;
								i.pmd.textSet = i.pmd.ts;
								i.pmd.subType = i.pmd.st;

								let duedate = i.edd;
								let startOrCreateDate = i.d || i.c;

								if (offlineAssignmentMap[i.aid]) {
									let offlineAssignment = offlineAssignmentMap[i.aid];
									let submitData = offlineAssignment.submitData;
									let bok = offlineAssignment.bok;
									if (i.pmd.pt === 'AAD') {
										if (submitData || bok) {
											i.inProgress = true;
											i.hasSubmission = true;
										}
									} else if (submitData) {
										i.submitted = true;
										i.timeSubmitted = submitData.ts;
									}

									if ((i.ed && i.ed !== offlineAssignment.ed) || (i.edd && i.edd !== offlineAssignment.edd)) {
										assignmentsToUpdate.push(i);
									}
								}

								if (duedate) {
									// Use student specific edd over assignment
									if (i.submitted && duedate < i.timeSubmitted) {
										// Student submitted past the deadline
										i.overdue = true;
									} else if (!i.submitted && duedate < now) {
										// Student hasn't submitted and deadline is past
										i.overdue = true;
									}
								}

								if (i.pmd.pt === 'P') {
									let img = i.pmd.img;
									let articleList = [];
									Object.keys(i.pmd.articles).forEach(pid => {
										let article = i.pmd.articles[pid];
										if (article.video) {
											i.pmd.hasVideo = true;
										}
										if (!img) {
											img = article.img;
										}
										articleList.push(article);
									});
									i.pmd.img = img;
									i.pmd.articleList = articleList;
								} else if (i.pmd.pt === 'AAD') {
									const mappedPMD = beatriceDataMap.product.serverToClient(i.pmd);

									i.pmd.allArticles = mappedPMD.allArticles;
								}

								if (i && i.opts && i.opts.bok && i.hasSubmission && startOrCreateDate > lastYear) {
									this.bokAssigned = true;
								}
							});

							if (assignmentsToUpdate.length) {
								// console.log('need to update assignments');
								return updateOfflineAssignments(assignmentsToUpdate);
							}
							return Q();
						})
						.catch(e => {
							logger.error(e);
							this.className = null;
							this.classCode = null;
							this.assignments = [];
						})
						.finally(() => (this.fetchingAssignments = false));
				}

				return undefined;
			},
			isAssignmentSubmitted,
			getAssignmentDisplayTime,

			goToPage(pg) {
				let filteredAssignments = this.filteredAssignments;
				let assignmentCount = filteredAssignments ? filteredAssignments.length : 0;
				let pageSize = this.pageSize;
				let offset = 0;

				if (assignmentCount > 0 && assignmentCount >= pg * pageSize) {
					offset = pg * pageSize;
					if (offset !== model.get('studentOffset')) {
						model.set('studentOffset', offset);
						scrollManager(0, {
							priority: 1,
							duration: 200
						});
					}
				}
			},
			storeListState() {
				let studentTab = this.studentTab;
				let studentOffset = this.offset;

				model.set('studentAssignmentsBackLink', {
					studentTab,
					studentOffset
				});
			},
			generateAssignmentLink(aid) {
				return '/student-workspace/' + aid;
			},
			onRefreshClick() {
				if (!this.fetchingAssignments) {
					this.getAssignments().then(() => this.goToPage(0));
				}
			},
			onBBOKArticleClick(article) {
				let pid = article.pid;
				let aid = article.aid;

				model.set('subArticleLink', pid);
				this.storeListState();
				page(`/student-workspace/${aid}#!wsTab:text/`);
			},
			onLibraryAdClick() {
				fireEvent(SL_LIBRARY_OPEN, {
					loc: 'PROMO_BOX'
				});
			},
			onAssignmentClick({ aid }) {
				let studentNotifications = this.studentNotifications;
				//Notification is unread
				if (studentNotifications && aid && studentNotifications[aid]) {
					readNotification(aid);
				}
			},
			getFirstStudentResearchAssessment() {
				getStudentResearchAssessments().then(([assessment]) => {
					this.researchAssessment = assessment;
				});
			},
			onlineOnlyChecks() {
				if (this.offlineMode === false) {
					if (userContext.getStudentType() === 'google') {
						classService.getClassesFromGoogleToken().then(results => {
							this.classes = results.classes;
						});
					} else if (userContext.getStudentType() === 'clever') {
						classService.getClassesFromCleverToken().then(results => {
							this.classes = results;
						});
					}
					// studentGetCVAAssignment();
					studentGetSKIAssignment();
					this.getFirstStudentResearchAssessment();
					getNotifications();
				}
			},
			onDownloadClick(item) {
				let offlineAssignmentsMap = this.offlineAssignmentsMap || {};
				let prms = null;
				this.offlineLoading = _.assign({}, this.offlineLoading, {
					[item.aid]: true
				});
				if (offlineAssignmentsMap[item.aid]) {
					prms = clearAssignment(item);
				} else {
					prms = downloadAssignment(item).then(() => {
						if (!this.hasDownloadedAnAssignment) {
							utils.openModal('#modal-download-assignment');
						}
					});
				}

				prms
					.catch(() => {
						alertUtilities.error('Assignment failed to download. Please try again later.');
					})
					.finally(() => {
						this.offlineLoading = _.assign({}, this.offlineLoading, {
							[item.aid]: false
						});
					});
			},
			authenticateWithGoogle() {
				authenticateWithGoogleClassroom();
			},
			uploadWorkspaces() {
				this.uploadingWorkspaces = true;
				return uploadOfflineWorkspaces()
					.then(() => this.getAssignments())
					.catch(e => logger.error('err', e))
					.finally(() => (this.uploadingWorkspaces = false));
			},
			clearAllOffline() {
				clearAll();
			},
			onOfflineToggle(item) {
				if (item && item.value) {
					this.sortAssignments(this.assignments, null, item.value);
				}
			},
			sortAssignments(assignments, offlineAssignmentsMap, mode) {
				offlineAssignmentsMap = offlineAssignmentsMap || this.offlineAssignmentsMap || {};
				assignments = assignments || this.assignments;

				let offlineMode = typeof mode !== 'undefined' ? mode : this.offlineMode;
				let offlineAssignments = Object.keys(offlineAssignmentsMap || {});
				this.assignments = assignments.sort(sortAssignmentsBySubmittedThenCreationTime);

				if (offlineAssignments.length && offlineMode) {
					// If Offline Mode, saved assignments at top
					this.assignments = this.assignments.sort((a, b) => {
						let aOffline = offlineAssignmentsMap[a.aid] ? 1 : 0;
						let bOffline = offlineAssignmentsMap[b.aid] ? 1 : 0;
						return bOffline - aOffline;
					});
				}
			}
		},
		watch: {
			bokAssigned(val) {
				if (val) {
					this.tabs = [...tabCfg];
				}
			},
			offlineMode: {
				handler(value) {
					if (value === false) {
						this.onlineOnlyChecks();
					}
				},
				immediate: true
			}
		},
		created() {
			setTimeout(() => {
				this.$emit('data-ready');
			}, 10);

			pubsub.subscribe('logged-out', () => {
				this.assignments = []; // clear out assignments
				model.remove('studentTab');

				this.className = '';
				this.classCode = '';

				return true;
			});

			if (typeof model.get('studentOffset') === 'undefined' || model.get('studentOffset') === null) {
				model.set('studentOffset', 0);
			}

			getAllOfflineAssignmentData()
				.then(offlineAssignments => {
					return this.getAssignments(offlineAssignments);
				})
				.catch(e => {
					return this.getAssignments([]);
				});

			this.$watchModel('studentTab', (val, initial) => {
				if (!initial) {
					model.set('studentOffset', 0);
				}
			});

			this.$watch(
				function () {
					return this.offlineMode + ',' + this.userOfflineWorkspaceData + ',' + this.needsGoogleAuth;
				},
				status => {
					if (!this.uploadingWorkspaces && !this.needsGoogleAuth && this.offlineMode === false && this.userOfflineWorkspaceData) {
						this.uploadWorkspaces();
					} else if (!this.uploadingWorkspaces && !this.needsGoogleAuth && this.offlineMode === false) {
						//Same conditions, just no work to upload
						clearOldAssignments();
					}
				}
			);

			model.remove('studentAssignmentsBackLink');

			if (typeof this.onReady === 'function') {
				this.onReady();
			}

			getOfflineModeStatus();
			this.onlineOnlyChecks();
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';

	main.main.student {
		.student-header-content {
			display: flex;
			justify-content: flex-end;
			flex-wrap: wrap;
		}

		.main-header-title {
			flex: 1 0 calc(100% - 11.5rem);
		}

		.student-header-actions {
			flex: 0 0 11.5rem;
			display: flex;
			flex-direction: column;
			align-items: flex-end;
		}

		.articles-list .assignment-container {
			position: relative;
			border-top: 1px solid $c-grey;
			padding-top: 0.5rem;

			.article {
				flex: 1;

				.article-image {
					float: left;
					margin-right: 0.5rem;
				}

				.article-content {
					a.button.-small {
						margin-bottom: 0.25rem;
					}
				}

				footer.article-meta {
					display: none;
				}

				.offline-download {
					padding: 0.25rem 0.5rem;

					&.active {
						color: $c-blue;
					}
				}

				button.offline-download.active:hover {
					color: $c-white;
					background-color: $c-red;
					border-color: $c-red;
				}
			}

			.article-classification {
				text-align: right;
				margin-bottom: 0.5rem;
				float: none;
				width: auto;

				> * {
					display: inline-block;
				}

				&.desktop {
					display: none;
				}
			}

			&.unread {
				position: relative;

				.title-container h2 {
					font-weight: bold;
				}
			}

			.disabled-mask {
				display: none;
			}

			&.disabled {
				.article-image {
					display: none;
				}

				.disabled-mask {
					display: block;
					position: absolute;
					top: 0;
					bottom: 0;
					right: 0;
					left: 0;
					opacity: 0.7;
					background-color: $c-grey;
				}
			}
		}

		.cva-title {
			font-size: 1.5rem;
		}

		.main-header {
			position: sticky;
			top: 0;
			z-index: 1;

			.tab-container {
				.button,
				button {
					position: relative;

					&.has-notification {
						margin-right: 2.5rem;
					}

					.notification-bubble {
						position: absolute;
						top: -8px;
						right: -1.75rem;
						display: inline-block;
						line-height: 8px;
						padding: 0.5rem;
						border-radius: 1rem;
						font-size: 1rem;
						height: 25px;
						background-color: $c-blue;
						color: white;
					}
				}
			}

			.offline-mode-toggle {
				display: flex;
				flex-direction: column;
				align-items: flex-end;

				> label {
					font-weight: 500;
				}
			}
		}

		.assignments-header {
			border-bottom: none;
			padding-bottom: 0.5rem;
			margin-bottom: 0.5rem;

			h2 {
				display: block;
			}

			h3 {
				display: none;
				flex: 1;
				text-align: right;
				font-size: 1rem;
				font-weight: normal;
				color: $c-slate-dark;
			}

			.refresh-button {
				font-size: 0.75rem;

				&.disabled {
					opacity: 0.6;
					color: white;
					background-color: $c-slate-light;
					border-color: $c-slate-light;
				}
			}
		}

		.main-content.wrapper {
			display: flex;
			max-width: $max-width;
			flex-direction: column-reverse;

			section {
				flex: 1;
				flex-basis: 28rem;
				flex-shrink: 0;
			}

			.student-sidebar {
				flex-basis: 20%;
				padding: 1rem;

				.library-promo {
					display: flex;
					flex-direction: row;
					align-items: center;
					justify-content: center;
					background: linear-gradient(132deg, #fff 49%, #fff 50%, $c-grey-extra-light 51%, $c-grey-extra-light 100%);
					text-align: center;
					max-height: 25rem;
					padding: 1rem;
					border: 1px solid $c-grey;
					border-radius: 7px;

					svg {
						height: 10rem;
					}

					h3 {
						margin-bottom: 0;
					}

					.sl-prom-body {
						max-width: 100%;
					}
				}
			}

			.offline-upload {
				width: 100%;
				border-radius: 4px;
				border: 1px solid $c-grey;
				padding: 2rem;

				.progress {
					display: block;
					height: 2rem;
					border-radius: 4px;
				}
			}
		}

		.assignment-callout {
			display: flex;
			padding: 1rem;
			margin-bottom: 1rem;

			.callout-icon {
				width: 3.25rem;
				height: 3.25rem;
				margin-right: 1rem;
			}

			.callout-body {
				flex: 1;
			}

			.callout-title-container {
				display: flex;
			}

			.callout-title {
				font-size: 1.25rem;
				flex: 1;
			}

			.callout-text {
				margin: 0.5rem 0;
			}

			.callout-date {
				flex: 0 1;
				margin-left: 0.25rem;
				padding-top: 0.5rem;
			}

			.callout-action-container {
				margin-top: 0.5rem;
				text-align: right;
			}
		}
	}

	@media only screen and (min-width: $bp-width-med) {
		main.main.student {
			.articles-list .assignment-container {
				display: flex;

				.article-classification {
					&.desktop,
					& > * {
						display: block;
					}

					&.mobile {
						display: none;
					}
				}

				.article {
					.article-image {
						width: 5rem;

						.article-featured-image-container {
							height: 5rem;
							width: 5rem;
						}
					}
				}
			}

			.assignments .assignments-header {
				display: flex;

				h3 {
					display: block;
					flex: 1;
					text-align: right;
					margin: 0.75em 0 0 0;
				}

				.refresh-button {
					margin: $size-xs 0 0 $size-medium;
					float: none;
				}
			}

			.main-content.wrapper {
				flex-direction: row;

				.student-sidebar {
					.library-promo {
						flex-direction: column;

						svg {
							width: 13rem;
							height: auto;
						}
					}
				}
			}

			.assignment-callout {
				.callout-icon {
					width: 4.35rem;
					height: 4.35rem;
					margin-right: 1.5rem;
				}

				.callout-title {
					font-size: 1.35rem;
				}
			}
		}
	}

	@media only screen and (min-width: $bp-width-lg) {
		main.main.student {
			.assignments .assignments-header {
				display: block;
			}
		}
	}
</style>
<template>
	<main
		id="content"
		class="main student">
		<header class="main-header -has-nav">
			<div class="wrapper">
				<div class="student-header-content">
					<h1 class="main-header-title">
						<span v-if="className">{{ className }}</span>
						<span v-if="!className">Join Class</span>
					</h1>
					<div class="student-header-actions">
						<offline-mode-toggle
							v-if="showOfflineToggle"
							@click-item="onOfflineToggle"
							:disabled="anyOfflineLoading || offlineDisabled">
						</offline-mode-toggle>
						<button
							v-if="offlineMode === false && classes.length == 1"
							data-modal="#add-class-student"
							class="button -outline margin-top-medium">
							+ Add a Class
						</button>
						<button
							@click="myClasses"
							v-if="offlineMode === false && classes.length > 1"
							class="button -outline margin-top-medium">
							My Classes
						</button>
					</div>
				</div>
				<tab-container
					model-key="studentTab"
					:default-tab="ASSIGNMENTS_TO_DO_TAB_FILTER">
					<template v-for="tab in tabs">
						<button
							:data-tab="tab.id"
							v-if="typeof offlineMode !== 'undefined' && (offlineModeInProgress === false || tab.id === ASSIGNMENTS_TO_DO_TAB_FILTER)"
							:class="{
								active: studentTab === tab.id,
								'has-notification': tab.id === ASSIGNMENTS_SUBMITTED_TAB_FILTER && notificationCount
							}">
							{{ tab.text }}
							<span
								v-if="tab.id === ASSIGNMENTS_SUBMITTED_TAB_FILTER && notificationCount"
								class="notification-bubble">
								{{ notificationCount }}
							</span>
						</button>
					</template>
				</tab-container>
			</div>
		</header>
		<div
			class="main-content wrapper text-centered"
			v-if="offlineMode === false && userOfflineWorkspaceData"
			ref="studentTab"
			:data-panel="studentTab">
			<span
				class="offline-upload"
				v-if="!needsGoogleAuth">
				<div v-if="uploadingWorkspaces">
					<div class="progress">
						<div
							class="progress-bar progress-active"
							style="width: 100%"></div>
					</div>
					Uploading Offline Assignments...
				</div>
				<div v-if="!uploadingWorkspaces && userOfflineWorkspaceData">
					Assignment upload failed. Would you like to try again?
					<div class="margin-top-medium">
						<button
							class="button"
							@click="uploadWorkspaces">
							Retry Upload
						</button>
						<button
							v-if="workspaceUploadFailures > 2"
							class="button -danger"
							@click="clearAllOffline">
							Clear All Offline
						</button>
					</div>
				</div>
			</span>
			<span
				class="offline-upload text-centered"
				v-if="needsGoogleAuth">
				You have offline assignments that need to be uploaded. Please authenticate with Google.
				<span
					class="google-auth-button button -large -wide -centered margin-bottom-small"
					@click="authenticateWithGoogle">
					Authenticate With Google
				</span>
			</span>
		</div>
		<div
			v-else
			class="main-content wrapper"
			ref="studentTabPanel"
			:data-panel="studentTab">
			<section
				class="assignments"
				v-if="studentTab !== BIG_BOOK_OF_KNOWLEDGE_TAB_FILTER">
				<header
					class="assignments-header"
					v-if="className">
					<h2>
						<template v-if="studentTab === 'c'">
							<template v-if="showEmptyMessage"> You Have No Assignments To Do </template>
							<template v-else> Assignments To Do </template>
						</template>
						<template v-if="studentTab === 's'">Submitted Assignments</template>
					</h2>
					<button
						v-if="offlineMode === false && (studentTab === 's' || studentTab === 'c')"
						class="refresh-button button -outline"
						:class="{
							disabled: fetchingAssignments
						}"
						@click="onRefreshClick"
						aria-label="Refresh">
						<i
							class="fa-solid fa-rotate fa-lg"
							aria-hidden="true"></i>
					</button>
					<h3>
						<template v-if="studentTab === 'c' && assignmentPage.length">Due Date</template>
						<template v-if="studentTab === 's' && assignmentPage.length">Date Submitted</template>
					</h3>
				</header>
				<div
					v-if="showButterBar && studentCVAAssignment"
					class="butter-bar">
					<div>
						<a
							class="cva-title"
							href="/student-cva-workspace"
							>Vocabulary Quest<sup>beta</sup></a
						>
						<span class="cva-date pull-right">Today</span>
					</div>
					<div class="text-centered">
						<a href="/student-cva-workspace">
							<button class="button cva-button">Start</button>
						</a>
					</div>
				</div>
				<div
					v-if="showButterBar && researchAssessment && sraButterBarContent"
					class="butter-bar assignment-callout sra-butter-bar">
					<assessment-icon class="callout-icon"></assessment-icon>
					<div class="callout-body">
						<div class="callout-title-container">
							<a
								class="callout-title"
								href="/student-research-assessment-workspace"
								>{{ sraButterBarContent.title }}</a
							>
							<span class="callout-date">Today</span>
						</div>
						<p
							class="callout-text"
							v-if="sraButterBarContent.bodyText">
							{{ sraButterBarContent.bodyText }}
						</p>
						<div class="callout-action-container">
							<a
								class="button -large sra-butter-bar-action"
								:href="`/student-research-assessment-workspace#!sraid:${researchAssessment.sraid}`">
								{{ sraButterBarContent.buttonText }}
							</a>
						</div>
					</div>
				</div>
				<div
					v-if="showButterBar && skiAssignment"
					class="butter-bar assignment-callout ski-butter-bar">
					<smiley-icon class="callout-icon"></smiley-icon>
					<div class="callout-body">
						<div class="callout-title-container">
							<a
								class="callout-title"
								href="/student-knowledge-inventory-workspace"
								>Reading Mindset Snapshot</a
							>
							<span class="callout-date">Today</span>
						</div>
						<div class="callout-action-container">
							<a
								href="/student-knowledge-inventory-workspace"
								class="button -large">
								Start
							</a>
						</div>
					</div>
				</div>
				<div class="assignments-content">
					<div
						class="articles-list"
						aria-live="polite">
						<template v-for="item in assignmentPage">
							<div
								class="assignment-container"
								:class="{
									unread: notifications[item.aid],
									disabled: offlineMode && !offlineAssignmentsMap[item.aid]
								}">
								<div class="disabled-mask"></div>
								<div class="article-classification mobile">
									<overdue-icon v-if="item.overdue"></overdue-icon>
									<graded-icon v-if="typeof notifications[item.aid] !== 'undefined'"></graded-icon>
									<time class="article-postdate">
										{{ getAssignmentDisplayTime(item) | date }}
									</time>
									<time
										v-if="item.timeSubmitted || (item.ed && isAssignmentSubmitted(item))"
										class="article-postdate">
										{{ (item.timeSubmitted || item.ed) | time }}
									</time>
								</div>
								<article-result-content
									:article="item.pmd"
									:product-link="getLink(item, assignment)"
									@click.native="onAssignmentClick(item)">
									<div slot="footer-tools">
										<template
											v-if="offlineModeAvailable === true && studentTab === ASSIGNMENTS_TO_DO_TAB_FILTER && !(item.pmd.pt === 'P' && item.pmd.hasVideo)">
											<div v-if="offlineMode === false">
												<button
													class="offline-download button -outline"
													:class="{
														active: offlineAssignmentsMap[item.aid]
													}"
													:disabled="typeof offlineMode === 'undefined' || anyOfflineLoading"
													@click.prevent="onDownloadClick(item)">
													<template v-if="!offlineLoading[item.aid] && !offlineAssignmentsMap[item.aid]">
														<i
															class="fa-solid fa-download"
															aria-hidden="true"></i>
														Download
													</template>
													<template v-if="!offlineLoading[item.aid] && offlineAssignmentsMap[item.aid]">
														<i
															class="fa-solid fa-download"
															aria-hidden="true"></i>
														Saved
													</template>
													<i
														class="fa-solid fa-spinner fa-spin"
														v-if="offlineLoading[item.aid]"
														aria-hidden="true"></i>
												</button>
											</div>
											<div
												v-if="offlineMode && offlineAssignmentsMap[item.aid]"
												class="offline-download active">
												<i
													class="fa-solid fa-download"
													v-if="!offlineLoading[item.aid]"
													aria-hidden="true"></i>
												Saved
											</div>
										</template>
										<a
											v-if="isAssignmentSubmitted(item)"
											@click="storeListState"
											:href="`/student-workspace/${item.aid}`"
											class="button -small see-results">
											See Your Results
										</a>
										<div v-if="isAssignmentSubmitted(item) && notifications[item.aid]">This assignment has been fully graded.</div>
										<template v-if="!isAssignmentSubmitted(item) && (item.redo || item.revise)">
											Your teacher has reassigned this.
											<br />
											<a
												class="button -small"
												v-bind:href="getLink(item, assignment)"
												@click="storeListState">
												<template v-if="item.redo"> Redo </template>
												<template v-else> Revise </template>
											</a>
										</template>
										<template v-if="item.pmd.pt === 'AAD' && item.inProgress && !isAssignmentSubmitted(item)">
											<div>
												<template v-if="item.inProgress === true">Choose your next article to read.</template>
												<template v-if="item.inProgress !== true">{{ item.inProgress.title }}</template>
											</div>
											<a
												:href="getLink(item, assignment)"
												@click="storeListState"
												class="button -small">
												Continue
											</a>
											<a
												v-if="item.hasSubmission"
												@click="storeListState"
												:href="`/student-workspace/${item.aid}#!wsTab:results`"
												class="button -outline -small">
												Progress
											</a>
										</template>
									</div>
								</article-result-content>
								<div class="article-classification desktop">
									<time class="article-postdate">
										{{ getAssignmentDisplayTime(item) | date }}
									</time>
									<time
										v-if="item.timeSubmitted || (item.ed && isAssignmentSubmitted(item))"
										class="article-postdate">
										{{ (item.timeSubmitted || item.ed) | time }}
									</time>
									<div>
										<overdue-icon v-if="item.overdue"></overdue-icon>
										<graded-icon v-if="typeof notifications[item.aid] !== 'undefined'"></graded-icon>
									</div>
								</div>
							</div>
						</template>
					</div>
					<!-- end .articles-list -->
					<pagination
						v-if="filteredAssignments.length > pageSize"
						:page="currentPage"
						:total="filteredAssignments.length"
						:on-page-change="goToPage"
						:page-size="pageSize">
					</pagination>
				</div>
			</section>
			<section v-if="studentTab === BIG_BOOK_OF_KNOWLEDGE_TAB_FILTER">
				<big-book-of-knowledge
					:assignments="assignments"
					:generate-assignment-link="generateAssignmentLink"
					:on-title-click="storeListState"
					:on-article-click="onBBOKArticleClick"
					:loading="fetchingAssignments">
				</big-book-of-knowledge>
			</section>
			<div
				class="student-sidebar"
				v-if="showStudentSidebar">
				<div class="library-promo">
					<sign-up-icon></sign-up-icon>
					<div class="sl-prom-body">
						<h3>Go to Your Library</h3>
						<p>Where you can read about the topics you’re interested in and discover something new!</p>
						<a
							href="/student-library"
							@click="onLibraryAdClick"
							class="button">
							Let's go!
						</a>
					</div>
				</div>
			</div>
		</div>
		<download-assignment-modal></download-assignment-modal>
		<!-- end .bottom -->
		<div
			id="add-class-student"
			class="modal"
			data-modal-window>
			<div class="modal-container">
				<div class="modal-window">
					<div class="modal-window-wrap">
						<button
							class="modal-close"
							data-modal-close>
							<span aria-hidden="true">&times;</span><span class="hidden-visually"> Close</span>
						</button>
						<section class="modal-content">
							<header class="modal-content-header">
								<h2>Add a Class</h2>
							</header>
							<div class="modal-content-main">
								<form-submitter
									form-name="addClassForm"
									:form-data.sync="addClassForm"
									:submit-to="addClass"
									action="./"
									class="form"
									no-close-modal
									novalidate>
									<auto-form-row
										v-bind:data.sync="addClassForm.classCode"
										placeholder="Enter Class Code"
										row-class="labelHiddenVisually"
										type="classCode"
										required>
									</auto-form-row>
									<auto-form-row
										v-bind:all-valid="addClassForm.allValid"
										type="submit"
										row-class="-full-width">
										Continue
									</auto-form-row>
								</form-submitter>
							</div>
						</section>
					</div>
				</div>
			</div>
		</div>
	</main>
</template>
